import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Loader2, Award, Search, Download, Eye, X, FlaskConical, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { fetchMisCertificados, blobCertificadoPorCodigo, blobCertificadoPrueba } from "../lib/academia";

function fmtFecha(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "long", year: "numeric" });
}

export default function MisCertificados() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null); // { url, titulo, codigo, demo }

  useEffect(() => {
    fetchMisCertificados().then(setItems).catch(() => setItems([]));
  }, []);

  const filtrados = useMemo(() => {
    if (!items) return [];
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((c) => (c.curso || "").toLowerCase().includes(t) || (c.codigo || "").toLowerCase().includes(t));
  }, [items, q]);

  async function verPrueba() {
    setBusy("prueba");
    try {
      const url = await blobCertificadoPrueba();
      setPreview({ url, titulo: "Certificado de prueba (muestra)", codigo: "CL-DEMO0000", demo: true });
    } catch { toast.error("No se pudo generar la muestra."); }
    finally { setBusy(null); }
  }

  async function verCert(c) {
    setBusy(c.codigo);
    try {
      const url = await blobCertificadoPorCodigo(c.codigo);
      setPreview({ url, titulo: c.curso, codigo: c.codigo, demo: false });
    } catch { toast.error("No se pudo cargar el certificado."); }
    finally { setBusy(null); }
  }

  function descargar() {
    if (!preview?.url) return;
    const a = document.createElement("a");
    a.href = preview.url;
    a.download = `certificado-${preview.codigo}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
  }

  function cerrar() {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  return (
    <div className="px-5 md:px-8 py-8 max-w-6xl mx-auto" data-testid="mis-certificados-page">
      <Link to="/academia/mis-cursos" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver a Mis Cursos
      </Link>

      <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl flex items-center justify-center bg-amber-100 text-amber-600"><Award size={22} /></span>
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Mis Certificados</h1>
            <p className="text-sm text-slate-500">Descarga los certificados de los cursos que completaste al 100%.</p>
          </div>
        </div>
        <button onClick={verPrueba} disabled={busy === "prueba"} data-testid="ver-certificado-prueba-btn"
          className="inline-flex items-center gap-2 border border-amber-400 text-amber-700 hover:bg-amber-50 font-semibold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-60">
          {busy === "prueba" ? <Loader2 size={16} className="animate-spin" /> : <FlaskConical size={16} />} Ver Certificado de Prueba
        </button>
      </div>

      <div className="mt-6 relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="cert-buscar" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por curso o código…"
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none" />
      </div>

      {items === null ? (
        <div className="mt-10 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 bg-white rounded-2xl border border-slate-100 shadow-soft p-10 text-center" data-testid="mis-certificados-vacio">
          <Award size={40} className="mx-auto text-slate-300" />
          <h3 className="mt-4 text-lg font-bold text-slate-900">Aún no tienes certificados</h3>
          <p className="mt-1 text-sm text-slate-500">Completa un curso al 100% y tu certificado se generará automáticamente aquí.</p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/academia/mis-cursos" className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-xl transition-colors">Ir a Mis Cursos</Link>
            <button onClick={verPrueba} className="inline-flex items-center gap-2 border border-amber-400 text-amber-700 hover:bg-amber-50 font-semibold px-5 py-2.5 rounded-xl transition-colors">
              <FlaskConical size={16} /> Ver Certificado de Prueba
            </button>
          </div>
        </div>
      ) : filtrados.length === 0 ? (
        <div className="mt-10 text-center text-slate-400" data-testid="cert-sin-resultados">Sin resultados para "{q}".</div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtrados.map((c) => (
            <div key={c.codigo} className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden" data-testid={`cert-card-${c.codigo}`}>
              <div className="h-24 flex items-center gap-3 px-5" style={{ background: "linear-gradient(135deg,#0A1428,#122548)" }}>
                <span className="w-11 h-11 rounded-xl bg-amber-400/90 text-slate-900 flex items-center justify-center flex-shrink-0"><ShieldCheck size={22} /></span>
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Certificado de aprobación</p>
                  <p className="text-white font-bold text-sm truncate">{c.curso}</p>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>Código</span><span className="font-mono font-semibold text-slate-700">{c.codigo}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                  <span>Emitido</span><span className="font-semibold text-slate-700">{fmtFecha(c.emitido_el)}</span>
                </div>
                {c.horas && (
                  <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                    <span>Duración</span><span className="font-semibold text-slate-700">{c.horas} h académicas</span>
                  </div>
                )}
                <div className="mt-4 flex items-center gap-2">
                  <button onClick={() => verCert(c)} disabled={busy === c.codigo} data-testid={`cert-ver-${c.codigo}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 bg-brand-navy hover:bg-brand-blue text-white font-semibold text-sm px-3 py-2 rounded-lg transition-colors disabled:opacity-60">
                    {busy === c.codigo ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Vista previa
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="cert-preview-modal" onClick={cerrar}>
          <div className="bg-white rounded-2xl w-full max-w-5xl h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <div className="min-w-0">
                <p className="font-bold text-slate-900 truncate">{preview.titulo}</p>
                <p className="text-xs text-slate-500">{preview.demo ? "Muestra · no válido como certificado oficial" : `Código ${preview.codigo}`}</p>
              </div>
              <div className="flex items-center gap-2">
                {!preview.demo && (
                  <button onClick={descargar} data-testid="cert-descargar-btn" className="inline-flex items-center gap-1.5 bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold text-sm px-4 py-2 rounded-lg transition-colors">
                    <Download size={15} /> Descargar PDF
                  </button>
                )}
                <button onClick={cerrar} data-testid="cert-cerrar-btn" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><X size={18} /></button>
              </div>
            </div>
            <iframe title="Certificado" src={preview.url} className="flex-1 w-full" />
          </div>
        </div>
      )}
    </div>
  );
}
